import { Fragment, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RatingBadgeChip, Section, SectionHeader, SubsectionHeader } from '@/components/common';
import {
  useUpdateSkillRatings,
  useUpdateOverallSkillRating,
  useSkillAreaDetails,
} from '@/queries/useJobEnds';
import type {
  SkillRatings,
  SkillRatingScale,
  SkillDisplayMode,
  SkillArea,
  SkillRatingItem,
  SkillDetail,
} from '@/api/types/jobEnd.types';
import { RATING_LABELS } from '@/utils/ratingLabels';

// =============================================================================
// Constants
// =============================================================================

const SKILL_RATING_OPTIONS: SkillRatingScale[] = ['BT', 'OT_Minus', 'OT', 'OT_Plus', 'AT', 'O', 'NA'];
const OVERALL_RATING_OPTIONS: SkillRatingScale[] = ['BT', 'OT_Minus', 'OT', 'OT_Plus', 'AT', 'O'];

const INFO_TOOLTIP_TEXT = `ME (Meets Expectations)
Skill areas rated OT (On Track) and above.

ND (Needs Development)
Skill areas rated OT- (On Track Minus) and below.

OT- (On Track Minus)

Performance is broadly On Track, but there are specific areas where further development is needed to demonstrate consistent performance at this level.

OT+ (On Track Plus)

Performance is clearly On Track, with elements of strength that are approaching Above Track, though not yet consistently demonstrated.`;

// =============================================================================
// Shared Styles
// =============================================================================

const overallRatingRowStyles: SxProps<Theme> = {
  py: 1.5,
  px: 2,
  bgcolor: 'custom.gold',
};

const skillAreaWrapperStyles: SxProps<Theme> = {
  bgcolor: 'common.white',
  border: 1,
  borderColor: 'grey.300',
  mt: 1,
};

const skillRowContentStyles: SxProps<Theme> = {
  py: 1.5,
  px: 2,
};

const SKILL_RATINGS_ROWS_TEST_ID = 'skill-ratings-rows';
const MAIN_AREA_BANNER_TEST_ID = 'main-area-banner';
const OVERALL_STANDARDS_TABLE_TEST_ID = 'overall-standards-table';

const STANDARDS_LINK_WIDTH = 130;
const RATING_OPTION_WIDTH = 72;

// =============================================================================
// Main Component
// =============================================================================

interface SkillRatingsSectionProps {
  skillRatings: SkillRatings;
  canEdit: boolean;
  displayMode: SkillDisplayMode;
  jobEndId: number;
  error?: boolean;
  rowVersion?: string;
}

export function SkillRatingsSection({
  skillRatings,
  canEdit,
  displayMode,
  jobEndId,
  error,
  rowVersion,
}: SkillRatingsSectionProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [localOverallRating, setLocalOverallRating] = useState<SkillRatingScale | null>(
    skillRatings.overallRating
  );
  const [localSkillRatings, setLocalSkillRatings] = useState<Map<number, SkillRatingScale | null>>(
    () => {
      const map = new Map<number, SkillRatingScale | null>();
      skillRatings.skillAreas.forEach((area) => {
        area.skills.forEach((skill) => {
          map.set(skill.skillId, skill.rating);
        });
      });
      return map;
    }
  );
  const [expandedAreas, setExpandedAreas] = useState<Set<number>>(new Set());
  const [prevSkillRatings, setPrevSkillRatings] = useState(skillRatings);

  const { mutate: updateSkillRatings } = useUpdateSkillRatings();
  const { mutate: updateOverallRating } = useUpdateOverallSkillRating();

  // Sync local state when props change (adjusting state during rendering pattern)
  if (skillRatings !== prevSkillRatings) {
    setPrevSkillRatings(skillRatings);
    setLocalOverallRating(skillRatings.overallRating);
    const map = new Map<number, SkillRatingScale | null>();
    skillRatings.skillAreas.forEach((area) => {
      area.skills.forEach((skill) => {
        map.set(skill.skillId, skill.rating);
      });
    });
    setLocalSkillRatings(map);
  }

  const handleOverallRatingChange = (rating: SkillRatingScale) => {
    const previousRating = localOverallRating;
    setLocalOverallRating(rating);
    updateOverallRating(
      { id: jobEndId, rating, rowVersion },
      { onError: () => setLocalOverallRating(previousRating) }
    );
  };

  const handleAreaRatingChange = (area: SkillArea, rating: SkillRatingScale) => {
    const previousRatings = new Map(localSkillRatings);
    const newRatings = new Map(localSkillRatings);
    area.skills.forEach((skill) => newRatings.set(skill.skillId, rating));
    setLocalSkillRatings(newRatings);

    const ratingsArray: SkillRatingItem[] = Array.from(newRatings.entries()).map(
      ([id, r]) => ({ skillId: id, rating: r })
    );
    updateSkillRatings(
      { id: jobEndId, ratings: ratingsArray, rowVersion },
      { onError: () => setLocalSkillRatings(previousRatings) }
    );
  };

  const toggleAreaExpand = (areaId: number) => {
    setExpandedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const getAreaRating = (area: SkillArea): SkillRatingScale | null => {
    if (area.skills.length === 0) return null;
    return localSkillRatings.get(area.skills[0].skillId) ?? null;
  };

  const isSimplified = displayMode === 'simplified';

  return (
    <Section>
      <SectionHeader
        error={error}
        action={
          <Tooltip
            title={<Typography sx={{ whiteSpace: 'pre-line' }}>{INFO_TOOLTIP_TEXT}</Typography>}
            placement="bottom-end"
            arrow
          >
            <IconButton size="small" sx={{ color: 'common.white' }} aria-label="Rating scale information">
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        }
      >
        RATINGS BY SKILL AREA
      </SectionHeader>
      <Box
        data-testid={SKILL_RATINGS_ROWS_TEST_ID}
        sx={{
          bgcolor: 'grey.200',
          ...(error && {
            outline: 2,
            outlineColor: 'error.main',
          }),
        }}
      >
        <OverallRatingRow
          rating={localOverallRating}
          onChange={handleOverallRatingChange}
          canEdit={canEdit}
          isSimplified={isSimplified}
          error={error && localOverallRating === null}
          jobEndId={jobEndId}
          standardsAreaId={skillRatings.overallStandardsAreaId}
          isExpanded={
            skillRatings.overallStandardsAreaId != null &&
            expandedAreas.has(skillRatings.overallStandardsAreaId)
          }
          onToggleExpand={() => {
            if (skillRatings.overallStandardsAreaId != null) {
              toggleAreaExpand(skillRatings.overallStandardsAreaId);
            }
          }}
        />
        {skillRatings.skillAreas.map((area, index) => {
          const previousMainArea = index > 0 ? skillRatings.skillAreas[index - 1].mainArea : null;
          const startsGroup = area.mainArea && area.mainArea !== previousMainArea ? area.mainArea : null;

          // Banner and row are flat siblings on purpose - the E2E page object reaches an area row's
          // wrapper by walking up from its label, so wrapping a group would break it.
          return (
            <Fragment key={area.areaId}>
              {startsGroup && <MainAreaBannerRow name={startsGroup} />}
              <SkillAreaRow
                area={area}
                index={index + 1}
                jobEndId={jobEndId}
                rating={getAreaRating(area)}
                onRatingChange={(rating) => handleAreaRatingChange(area, rating)}
                canEdit={canEdit}
                isSimplified={isSimplified}
                isDesktop={isDesktop}
                isExpanded={expandedAreas.has(area.areaId)}
                onToggleExpand={() => toggleAreaExpand(area.areaId)}
                error={error && getAreaRating(area) === null}
              />
            </Fragment>
          );
        })}
      </Box>
    </Section>
  );
}

// =============================================================================
// Main Area Banner Row
// =============================================================================

interface MainAreaBannerRowProps {
  name: string;
}

function MainAreaBannerRow({ name }: MainAreaBannerRowProps) {
  return (
    <Box sx={{ mt: 1 }} data-testid={MAIN_AREA_BANNER_TEST_ID}>
      <SubsectionHeader>{name}</SubsectionHeader>
    </Box>
  );
}

// =============================================================================
// Rating Radio Group (reusable)
// =============================================================================

interface RatingRadioGroupProps {
  options: SkillRatingScale[];
  value: SkillRatingScale | null;
  onChange: (rating: SkillRatingScale) => void;
  disabled: boolean;
  size?: 'small' | 'medium';
  variant?: 'body2' | 'caption';
}

function RatingRadioGroup({
  options,
  value,
  onChange,
  disabled,
  size = 'small',
  variant = 'body2',
}: RatingRadioGroupProps) {
  return (
    <>
      {options.map((option) => (
        <Stack key={option} direction="row" alignItems="center" width={RATING_OPTION_WIDTH}>
          <Radio
            value={option}
            size={size}
            disabled={disabled}
            checked={value === option}
            onChange={() => onChange(option)}
            sx={{ p: size === 'small' ? 0.5 : 0.25 }}
          />
          <Typography variant={variant}>{RATING_LABELS[option]}</Typography>
        </Stack>
      ))}
    </>
  );
}

// =============================================================================
// Overall Rating Row
// =============================================================================

interface OverallRatingRowProps {
  rating: SkillRatingScale | null;
  onChange: (rating: SkillRatingScale) => void;
  canEdit: boolean;
  isSimplified: boolean;
  error?: boolean;
  jobEndId: number;
  standardsAreaId: number | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

function OverallRatingRow({
  rating,
  onChange,
  canEdit,
  isSimplified,
  error,
  jobEndId,
  standardsAreaId,
  isExpanded,
  onToggleExpand,
}: OverallRatingRowProps) {
  const { data: skillDetails, isLoading } = useSkillAreaDetails(
    jobEndId,
    standardsAreaId,
    isExpanded
  );

  const rowStyles = {
    ...overallRatingRowStyles,
    ...(error && { backgroundColor: '#fff0f0' }),
  };
  const labelStyles = error ? { color: 'error.main' } : undefined;

  const standardsLink =
    standardsAreaId == null ? null : (
      <ShowStandardsLink
        isExpanded={isExpanded}
        isLoading={isLoading}
        onToggle={onToggleExpand}
        ariaExpanded={isExpanded}
        ariaLabel={`${isExpanded ? 'Hide' : 'Show'} standards for overall rating`}
      />
    );

  const row = isSimplified ? (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={rowStyles}>
      <Typography variant="subtitle1" fontWeight="bold" sx={labelStyles}>
        OVERALL RATING
      </Typography>
      {standardsLink === null ? (
        <RatingBadgeChip code={rating ?? 'NA'} labelMode="simplified" showTooltip />
      ) : (
        <Stack direction="row" alignItems="center" spacing={2}>
          <RatingBadgeChip code={rating ?? 'NA'} labelMode="simplified" showTooltip />
          {standardsLink}
        </Stack>
      )}
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" sx={rowStyles}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, ...labelStyles }}>
        OVERALL RATING
      </Typography>
      <Stack direction="row" alignItems="center">
        <RatingRadioGroup
          options={OVERALL_RATING_OPTIONS}
          value={rating}
          onChange={onChange}
          disabled={!canEdit}
        />
        <Box width={RATING_OPTION_WIDTH} />
        <Box width={STANDARDS_LINK_WIDTH}>{standardsLink}</Box>
      </Stack>
    </Stack>
  );

  if (standardsAreaId == null) {
    return row;
  }

  // Box, never Stack: hasOverallRatingError in the E2E page object resolves the first
  // MuiStack-root ancestor of the OVERALL RATING label to read the row's error background.
  return (
    <Box>
      {row}
      <Collapse in={isExpanded && !isLoading}>
        <SkillDescriptionsTable
          skillDetails={skillDetails}
          testId={OVERALL_STANDARDS_TABLE_TEST_ID}
        />
      </Collapse>
    </Box>
  );
}

// =============================================================================
// Skill Area Row
// =============================================================================

interface SkillAreaRowProps {
  area: SkillArea;
  index: number;
  jobEndId: number;
  rating: SkillRatingScale | null;
  onRatingChange: (rating: SkillRatingScale) => void;
  canEdit: boolean;
  isSimplified: boolean;
  isDesktop: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  error?: boolean;
}

function SkillAreaRow({
  area,
  index,
  jobEndId,
  rating,
  onRatingChange,
  canEdit,
  isSimplified,
  isDesktop,
  isExpanded,
  onToggleExpand,
  error,
}: SkillAreaRowProps) {
  const { data: skillDetails, isLoading } = useSkillAreaDetails(jobEndId, area.areaId, isExpanded);
  const areaLabel = `${index}. ${area.areaName}`;

  const errorStyles = error ? { backgroundColor: '#fff0f0' } : undefined;
  const errorLabelStyles = error ? { color: 'error.main', fontWeight: 600 } : undefined;

  // Simplified view (Appraisee/Mentor)
  if (isSimplified) {
    return (
      <Box sx={{ ...skillAreaWrapperStyles, ...errorStyles }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={skillRowContentStyles}>
          <Typography variant="body1" sx={errorLabelStyles}>{areaLabel}</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <RatingBadgeChip code={rating ?? 'NA'} labelMode="simplified" showTooltip />
            <ShowStandardsLink isExpanded={isExpanded} isLoading={isLoading} onToggle={onToggleExpand} />
          </Stack>
        </Stack>
        <Collapse in={isExpanded && !isLoading}>
          <SkillDescriptionsTable skillDetails={skillDetails} />
        </Collapse>
      </Box>
    );
  }

  // Full view - Desktop
  if (isDesktop) {
    return (
      <Box sx={{ ...skillAreaWrapperStyles, ...errorStyles }}>
        <Stack direction="row" alignItems="center" sx={{ ...skillRowContentStyles, py: 1 }}>
          <Typography variant="body1" sx={{ flex: 1, ...errorLabelStyles }}>
            {areaLabel}
          </Typography>
          <Stack direction="row" alignItems="center">
            <RatingRadioGroup
              options={SKILL_RATING_OPTIONS}
              value={rating}
              onChange={onRatingChange}
              disabled={!canEdit}
            />
            <Box width={STANDARDS_LINK_WIDTH}>
              <ShowStandardsLink isExpanded={isExpanded} isLoading={isLoading} onToggle={onToggleExpand} />
            </Box>
          </Stack>
        </Stack>
        <Collapse in={isExpanded && !isLoading}>
          <SkillDescriptionsTable skillDetails={skillDetails} />
        </Collapse>
      </Box>
    );
  }

  // Full view - Mobile
  return (
    <Box sx={{ ...skillAreaWrapperStyles, ...errorStyles }}>
      <Box sx={skillRowContentStyles}>
        <Typography variant="body1" fontWeight="medium" mb={1} sx={errorLabelStyles}>
          {areaLabel}
        </Typography>
        <Stack direction="row" flexWrap="wrap" spacing={0.5} mb={1}>
          <RatingRadioGroup
            options={SKILL_RATING_OPTIONS}
            value={rating}
            onChange={onRatingChange}
            disabled={!canEdit}
            variant="caption"
          />
        </Stack>
        <ShowStandardsLink isExpanded={isExpanded} isLoading={isLoading} onToggle={onToggleExpand} />
      </Box>
      <Collapse in={isExpanded && !isLoading}>
        <SkillDescriptionsTable skillDetails={skillDetails} />
      </Collapse>
    </Box>
  );
}

// =============================================================================
// Show Standards Link
// =============================================================================

interface ShowStandardsLinkProps {
  isExpanded: boolean;
  isLoading: boolean;
  onToggle: () => void;
  // Optional so the existing skill area rows keep their current markup unchanged
  ariaExpanded?: boolean;
  ariaLabel?: string;
}

function ShowStandardsLink({
  isExpanded,
  isLoading,
  onToggle,
  ariaExpanded,
  ariaLabel,
}: ShowStandardsLinkProps) {
  if (isLoading) {
    return <CircularProgress size={14} sx={{ ml: 2 }} />;
  }

  return (
    <Link
      component="button"
      variant="body2"
      onClick={onToggle}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      sx={{ ml: 2, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      <ExpandMoreIcon
        sx={{
          fontSize: 18,
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}
      />
      {isExpanded ? 'Hide standards' : 'Show standards'}
    </Link>
  );
}

// =============================================================================
// Skill Descriptions Table
// =============================================================================

const tableHeaderCellSx: SxProps<Theme> = {
  fontWeight: 'bold',
  bgcolor: 'custom.deepTeal',
  color: 'common.white',
};

const tableBodyCellSx: SxProps<Theme> = {
  verticalAlign: 'top',
  whiteSpace: 'pre-line',
};

interface SkillDescriptionsTableProps {
  skillDetails?: SkillDetail[];
  testId?: string;
}

function SkillDescriptionsTable({ skillDetails, testId }: SkillDescriptionsTableProps) {
  if (!skillDetails || skillDetails.length === 0) {
    return (
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary">
          No standards defined for this area.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Table size="small" data-testid={testId}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderCellSx, width: '15%' }}>SKILL (SUB-AREA)</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>BELOW TRACK</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>ON TRACK</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>ABOVE TRACK</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '21.25%' }}>OUTSTANDING</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {skillDetails.map((skill) => (
            <TableRow key={skill.skillId}>
              <TableCell sx={{ ...tableBodyCellSx, fontWeight: 'medium' }}>
                {skill.skillName}
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.btDescription || '-'}</Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.otDescription || '-'}</Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.atDescription || '-'}</Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography variant="body2">{skill.oDescription || '-'}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default SkillRatingsSection;
